import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';

const DEFAULT_UNIT = 'px';

const ActionsInsertImageIntoPdf = ({ caseid, tags, settingId, onSaveStatusChange, initialScope }) => {
  const { t } = useTranslation();
  const defaultSourceMode = initialScope === 'project' ? 'other' : 'current';
  const [sourceMode, setSourceMode] = useState(defaultSourceMode);
  const [sourcePdfPath, setSourcePdfPath] = useState('');
  const [imagePath, setImagePath] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const [pageNumber, setPageNumber] = useState('1');
  const [x, setX] = useState('');
  const [y, setY] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [unit, setUnit] = useState(DEFAULT_UNIT);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const sourcePdfRef = useRef(null);
  const imagePathRef = useRef(null);
  const targetPathRef = useRef(null);
  const pageNumberRef = useRef(null);
  const xRef = useRef(null);
  const yRef = useRef(null);
  const widthRef = useRef(null);
  const heightRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory: handleBrowseSourcePdf, FolderBrowserComponent: SourcePdfBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        setSourcePdfPath(selectedPath);
      }
    },
  });

  const { handleBrowseDirectory: handleBrowseImagePath, FolderBrowserComponent: ImageBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        setImagePath(selectedPath);
      }
    },
  });

  const { handleBrowseDirectory: handleBrowseTargetPath, FolderBrowserComponent: TargetBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (selectedPath) => {
      if (selectedPath) {
        const normalizedPath = String(selectedPath).replace(/[\\/]+$/, '');
        const separator = normalizedPath.includes('\\') ? '\\' : '/';
        setTargetPath(`${normalizedPath}${separator}{filename}_with_image.pdf`);
      }
    },
  });

  const customDataTags = useMemo(() => [], []);

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'insertimagetopdf',
    onSaveStatusChange,
    customComparison: (initialData) => {
      if (!initialData) return false;
      const initialSourcePath = String(initialData.sourceFilePath || initialData.sourcePdfPath || '');
      const sourcePathMatches = sourceMode === 'other'
        ? initialSourcePath === sourcePdfPath
        : true;
      return (
        String(initialData.sourceMode || 'current') === sourceMode
        && sourcePathMatches
        && String(initialData.imagePath || '') === imagePath
        && String(initialData.targetPath || '') === targetPath
        && String(initialData.pageNumber || '1') === pageNumber
        && String(initialData.x || '') === x
        && String(initialData.y || '') === y
        && String(initialData.width || '') === width
        && String(initialData.height || '') === height
        && String(initialData.unit || DEFAULT_UNIT).toLowerCase() === unit
      );
    },
    customSaveData: () => ({
      sourceMode,
      sourceFilePath: sourceMode === 'other' ? sourcePdfPath.trim() : '',
      imagePath: imagePath.trim(),
      targetPath: targetPath.trim(),
      pageNumber: pageNumber.trim(),
      x: x.trim(),
      y: y.trim(),
      width: width.trim(),
      height: height.trim(),
      unit,
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (error) {
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      setSourceMode(String(parsedData.sourceMode || defaultSourceMode));
      setSourcePdfPath(String(parsedData.sourceFilePath || parsedData.sourcePdfPath || ''));
      setImagePath(parsedData.imagePath || parsedData.sourceImagePath || '');
      setTargetPath(parsedData.targetPath || parsedData.target || '');
      setPageNumber(String(parsedData.pageNumber || parsedData.page_number || '1'));
      setX(String(parsedData.x ?? ''));
      setY(String(parsedData.y ?? ''));
      setWidth(String(parsedData.width ?? ''));
      setHeight(String(parsedData.height ?? ''));
      setUnit(String(parsedData.unit || DEFAULT_UNIT).toLowerCase());
    },
    enableConditions: true,
  });

  const handleMenuOpen = useCallback((event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuType('');
  }, []);

  const handleTagSelect = useCallback((value) => {
    if (menuType === 'sourcePdf') {
      sourcePdfRef.current?.insertTag(value);
    } else if (menuType === 'image') {
      imagePathRef.current?.insertTag(value);
    } else if (menuType === 'target') {
      targetPathRef.current?.insertTag(value);
    } else if (menuType === 'pageNumber') {
      pageNumberRef.current?.insertTag(value);
    } else if (menuType === 'x') {
      xRef.current?.insertTag(value);
    } else if (menuType === 'y') {
      yRef.current?.insertTag(value);
    } else if (menuType === 'width') {
      widthRef.current?.insertTag(value);
    } else if (menuType === 'height') {
      heightRef.current?.insertTag(value);
    }
    handleMenuClose();
  }, [handleMenuClose, menuType]);

  const handleSourceModeChange = useCallback((event) => {
    const nextMode = event.target.value;
    setSourceMode(nextMode);
    if (nextMode !== 'other') {
      setSourcePdfPath('');
    }
  }, []);

  const handleBrowseSourcePdfClick = useCallback(() => {
    handleBrowseSourcePdf();
  }, [handleBrowseSourcePdf]);

  const handleBrowseImageClick = useCallback(() => {
    handleBrowseImagePath();
  }, [handleBrowseImagePath]);

  const handleBrowseTargetClick = useCallback(() => {
    handleBrowseTargetPath();
  }, [handleBrowseTargetPath]);

  return (
    <ActionSettingsLayout
      title={t('workflow.insert_image_into_pdf.title', 'Insert Image into PDF')}
      isEnabled={actionSettings.isEnabled}
      onEnabledChange={actionSettings.setIsEnabled}
      isSaveDisabled={actionSettings.isSaveDisabled}
      onSave={actionSettings.handleSave}
      onForceRun={actionSettings.handleForceRun}
      forceRunLoading={actionSettings.forceRunLoading}
      conditions={actionSettings.conditions}
      onConditionsChange={actionSettings.handleConditionsChange}
      popUpOpen={actionSettings.popUpOpen}
      forceRunResult={actionSettings.forceRunResult}
      onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
      forceRunWarningOpen={actionSettings.forceRunWarningOpen}
      forceRunWarningCount={actionSettings.forceRunWarningCount}
      onForceRunConfirm={actionSettings.handleForceRunConfirm}
      onForceRunCancel={actionSettings.handleForceRunCancel}
      tags={tags}
      enableConditions
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <CardHeader
              title={<Typography sx={{ fontWeight: 700 }}>{t('workflow.insert_image_into_pdf.source_pdf_title', 'Source PDF')}</Typography>}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
            />
            <CardContent>
              <RadioGroup row value={sourceMode} onChange={handleSourceModeChange}>
                <FormControlLabel value="current" control={<Radio />} label={t('workflow.insert_image_into_pdf.use_current_source', 'Use current event file')} />
                <FormControlLabel value="other" control={<Radio />} label={t('workflow.insert_image_into_pdf.use_custom_source', 'Use custom PDF path')} />
              </RadioGroup>

              {sourceMode === 'other' && (
                <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
                  <TagEditor
                    caseid={caseid}
                    ref={sourcePdfRef}
                    value={sourcePdfPath}
                    onChange={setSourcePdfPath}
                    placeholder={t('workflow.insert_image_into_pdf.source_pdf_placeholder', '/path/to/source.pdf')}
                    singleLine
                    multiline={false}
                    disabled={sourceMode !== 'other'}
                    onTagClick={(event) => handleMenuOpen(event, 'sourcePdf')}
                    tags={tags}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleBrowseSourcePdfClick}
                      disabled={sourceMode !== 'other'}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('workflow.insert_image_into_pdf.browse_pdf', 'Browse PDF')}
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <CardHeader
              title={<Typography sx={{ fontWeight: 700 }}>{t('workflow.insert_image_into_pdf.image_title', 'Image to insert')}</Typography>}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
            />
            <CardContent>
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <TagEditor
                  caseid={caseid}
                  ref={imagePathRef}
                  value={imagePath}
                  onChange={setImagePath}
                  placeholder={t('workflow.insert_image_into_pdf.image_placeholder', '/path/to/image.png')}
                  singleLine
                  multiline={false}
                  onTagClick={(event) => handleMenuOpen(event, 'image')}
                  tags={tags}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<ImageIcon />}
                    onClick={handleBrowseImageClick}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('workflow.insert_image_into_pdf.browse_image', 'Browse Image')}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <CardHeader
              title={<Typography sx={{ fontWeight: 700 }}>{t('workflow.insert_image_into_pdf.placement_title', 'Placement')}</Typography>}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {t('workflow.insert_image_into_pdf.page_number', 'Page number')}
                  </Typography>
                  <TagEditor
                    caseid={caseid}
                    ref={pageNumberRef}
                    value={pageNumber}
                    onChange={setPageNumber}
                    placeholder="1"
                    singleLine
                    multiline={false}
                    minHeight="40px"
                    onTagClick={(event) => handleMenuOpen(event, 'pageNumber')}
                    tags={tags}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('workflow.insert_image_into_pdf.page_number_help', 'Pages are 1-based. If the X field contains a coordinate tag with page, this value can stay at 1.')}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {t('workflow.insert_image_into_pdf.x', 'X')}
                  </Typography>
                  <TagEditor
                    caseid={caseid}
                    ref={xRef}
                    value={x}
                    onChange={setX}
                    placeholder="0"
                    singleLine
                    multiline={false}
                    minHeight="40px"
                    onTagClick={(event) => handleMenuOpen(event, 'x')}
                    tags={tags}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('workflow.insert_image_into_pdf.x_help', 'Paste a coordinate tag here, for example {signature_coordinates}. The image will be centered on the tag box and the size comes from Width and Height.')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {t('workflow.insert_image_into_pdf.y', 'Y')}
                  </Typography>
                  <TagEditor
                    caseid={caseid}
                    ref={yRef}
                    value={y}
                    onChange={setY}
                    placeholder="0"
                    singleLine
                    multiline={false}
                    minHeight="40px"
                    onTagClick={(event) => handleMenuOpen(event, 'y')}
                    tags={tags}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {t('workflow.insert_image_into_pdf.width', 'Width')}
                  </Typography>
                  <TagEditor
                    caseid={caseid}
                    ref={widthRef}
                    value={width}
                    onChange={setWidth}
                    placeholder=""
                    singleLine
                    multiline={false}
                    minHeight="40px"
                    onTagClick={(event) => handleMenuOpen(event, 'width')}
                    tags={tags}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('workflow.insert_image_into_pdf.width_help', 'Leave empty or 0 to use the native image width.')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {t('workflow.insert_image_into_pdf.height', 'Height')}
                  </Typography>
                  <TagEditor
                    caseid={caseid}
                    ref={heightRef}
                    value={height}
                    onChange={setHeight}
                    placeholder=""
                    singleLine
                    multiline={false}
                    minHeight="40px"
                    onTagClick={(event) => handleMenuOpen(event, 'height')}
                    tags={tags}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('workflow.insert_image_into_pdf.height_help', 'Leave empty or 0 to use the native image height.')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {t('workflow.insert_image_into_pdf.unit', 'Unit')}
                  </Typography>
                  <FormControl fullWidth>
                    <Select 
                      value={unit} 
                      onChange={(event) => setUnit(String(event.target.value).toLowerCase())}
                      sx={{ height: '40px' }}
                    >
                      <MenuItem value="px">px</MenuItem>
                      <MenuItem value="mm">mm</MenuItem>
                      <MenuItem value="cm">cm</MenuItem>
                      <MenuItem value="pt">pt</MenuItem>
                      <MenuItem value="in">in</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <CardHeader
              title={<Typography sx={{ fontWeight: 700 }}>{t('workflow.insert_image_into_pdf.output_title', 'Output PDF')}</Typography>}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff' }}
            />
            <CardContent>
              <TagEditor
                caseid={caseid}
                ref={targetPathRef}
                value={targetPath}
                onChange={setTargetPath}
                placeholder={t('workflow.insert_image_into_pdf.target_placeholder', '/{filename}_with_image.pdf')}
                singleLine
                multiline={false}
                onTagClick={(event) => handleMenuOpen(event, 'target')}
                tags={tags}
              />
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<FolderOpenIcon />}
                  onClick={handleBrowseTargetClick}
                  sx={{ textTransform: 'none' }}
                >
                  {t('workflow.insert_image_into_pdf.browse_output', 'Browse Output Folder')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {SourcePdfBrowserComponent}
      {ImageBrowserComponent}
      {TargetBrowserComponent}
      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMenuClose}
        additionalTags={customDataTags}
        menuType={menuType}
      />
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsInsertImageIntoPdf);
